import { useMemo } from 'react';
import { useColorModeValue } from '@chakra-ui/react';
import { useDateRangeQueryString } from '@investigator/shared/hooks';
import { logScaleEntityPivot } from '@investigator/shared/utils';
import { MISSING_DATA_PLACEHOLDER } from '@investigator/shared/constants';
import { i18n } from '@investigator/shared/localization';
import {
  EntityCommunicationPodVariant,
  CommunicatingEntityDetail,
  CommunicationDirection,
} from '../types/entityCommunication';
import { useEntityCommunicationContext } from '../context';
import { useEntitiesContext } from '../context';
import {
  ENTITY_COMMUNICATION_ICONS,
  getEntityCardBackgroundColor,
  getEntityCardBorderColor,
  getEntityCardPrimaryText,
  getEntityCardSecondaryText,
  shouldShowEntityCardShadow,
  getEntityCardCount,
  isEntityCardClickable,
} from '../utils/entityCommunication';

const modalCopy = i18n.pages.entities.entityAnalysisModal;

/**
 * Props returned by useEntityCommunicationCard hook
 */
export interface UseEntityCommunicationCardResult {
  // Theme colors
  currentPodBg: string;
  currentPodBorder: string;
  otherPodBorder: string;
  shadowBg: string;
  // Icon
  Icon: React.ComponentType<Record<string, unknown>>;
  // Styling and behavior
  backgroundColor: string;
  borderColor: string;
  showShadow: boolean;
  isClickable: boolean;
  // Text content
  primaryText: string;
  secondaryText: string;
  // Data
  entities: CommunicatingEntityDetail[];
  entityName: string;
  entityCount: number;
  isLoading: boolean;
  // Query
  logSearchQuery: string;
  // Popover
  direction: CommunicationDirection | null;
}

/**
 * Comprehensive custom hook for entity communication card
 * Encapsulates all logic, theme, and data fetching
 */
export const useEntityCommunicationCard = (
  variant: EntityCommunicationPodVariant,
): UseEntityCommunicationCardResult => {
  const { selectedEntity } = useEntitiesContext();
  const { counts, inboundEntities, outboundEntities, isLoading } =
    useEntityCommunicationContext();
  const { start, end } = useDateRangeQueryString();

  // Theme-aware colors
  const currentPodBg = useColorModeValue('teal.100', 'teal.700');
  const currentPodBorder = useColorModeValue('teal.300', 'teal.500');
  const otherPodBorder = useColorModeValue('gray.400', 'gray.600');
  const shadowBg = useColorModeValue('gray.300', 'gray.700');

  // Entity name
  const entityName = selectedEntity?.entityName || MISSING_DATA_PLACEHOLDER;

  // Get the icon component for this variant
  const Icon = useMemo(() => ENTITY_COMMUNICATION_ICONS[variant], [variant]);

  // Build log search query for this entity based on variant
  const logSearchQuery = useMemo(() => {
    if (!start || !end || entityName === MISSING_DATA_PLACEHOLDER) {
      return '';
    }

    // Map variant to direction for logScaleEntityPivot
    let direction: 'inbound' | 'outbound' | 'all' = 'all';
    switch (variant) {
      case EntityCommunicationPodVariant.INBOUND:
        direction = 'inbound';
        break;
      case EntityCommunicationPodVariant.OUTBOUND:
        direction = 'outbound';
        break;
      case EntityCommunicationPodVariant.CURRENT:
      default:
        direction = 'all';
        break;
    }

    return logScaleEntityPivot({ start, end, entityName, direction });
  }, [start, end, entityName, variant]);

  // Get card styling and behavior
  const backgroundColor = getEntityCardBackgroundColor(variant, currentPodBg);
  const borderColor = getEntityCardBorderColor(
    variant,
    currentPodBorder,
    otherPodBorder,
  );
  const showShadow = shouldShowEntityCardShadow(variant);
  const entityCount = getEntityCardCount(variant, counts);
  const isClickable = isEntityCardClickable(variant, entityCount);

  // Get card text content with translations from i18n
  const primaryText = getEntityCardPrimaryText(variant, counts, {
    currentEntity: modalCopy.entityCommunication.currentEntity,
    entity: modalCopy.entityCommunication.entity,
    entities: modalCopy.entityCommunication.entities,
  });

  const secondaryText = getEntityCardSecondaryText(variant, entityName, {
    contactedCurrentEntity:
      modalCopy.entityCommunication.contactedCurrentEntity,
    contactedByCurrentEntity:
      modalCopy.entityCommunication.contactedByCurrentEntity,
  });

  // Get entities list
  const entities =
    variant === EntityCommunicationPodVariant.INBOUND
      ? inboundEntities
      : variant === EntityCommunicationPodVariant.OUTBOUND
        ? outboundEntities
        : [];

  // Get direction for popover
  const direction =
    variant === EntityCommunicationPodVariant.INBOUND
      ? CommunicationDirection.INBOUND
      : variant === EntityCommunicationPodVariant.OUTBOUND
        ? CommunicationDirection.OUTBOUND
        : null;

  return {
    currentPodBg,
    currentPodBorder,
    otherPodBorder,
    shadowBg,
    Icon,
    backgroundColor,
    borderColor,
    showShadow,
    isClickable,
    primaryText,
    secondaryText,
    entities,
    entityName,
    entityCount,
    isLoading,
    logSearchQuery,
    direction,
  };
};
